import React, { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { Code, Copy, Check, ChevronDown, ChevronRight, Lock, Globe, Server, Key, FileJson, Eye, EyeOff } from 'lucide-react'

const DeveloperApiPage = () => {
  const { user, token } = useAuth()
  const [copiedId, setCopiedId] = useState(null)
  const [expandedSections, setExpandedSections] = useState({
    auth: true,
    createDesign: true,
    visibility: true,
    listTemplates: false,
    getTemplate: false,
    updateTemplate: false,
    deleteTemplate: false,
    cloneTemplate: false,
  })
  const [showToken, setShowToken] = useState(false)

  const baseUrl = window.location.origin + '/api/v1'

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const CopyButton = ({ text, id }) => (
    <button
      onClick={() => copyToClipboard(text, id)}
      className="absolute top-2 right-2 p-1.5 rounded bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors"
      title="Copy to clipboard"
    >
      {copiedId === id ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  )

  const CodeBlock = ({ code, language = 'json', id }) => (
    <div className="relative">
      <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-sm overflow-x-auto font-mono">
        <code>{code}</code>
      </pre>
      <CopyButton text={code} id={id} />
    </div>
  )

  const MethodBadge = ({ method }) => {
    const colors = {
      GET: 'bg-green-100 text-green-800 border-green-300',
      POST: 'bg-blue-100 text-blue-800 border-blue-300',
      PUT: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      DELETE: 'bg-red-100 text-red-800 border-red-300',
    }
    return (
      <span className={`px-2.5 py-1 rounded text-xs font-bold border ${colors[method] || 'bg-gray-100 text-gray-800'}`}>
        {method}
      </span>
    )
  }

  const ParamRow = ({ name, type, required, description }) => (
    <tr className="border-b border-gray-100">
      <td className="py-2 px-3">
        <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded text-purple-700">{name}</code>
      </td>
      <td className="py-2 px-3 text-sm text-gray-600">{type}</td>
      <td className="py-2 px-3">
        {required ? (
          <span className="text-xs px-1.5 py-0.5 bg-red-50 text-red-700 rounded border border-red-200">Required</span>
        ) : (
          <span className="text-xs px-1.5 py-0.5 bg-gray-50 text-gray-600 rounded border border-gray-200">Optional</span>
        )}
      </td>
      <td className="py-2 px-3 text-sm text-gray-600">{description}</td>
    </tr>
  )

  const EndpointSection = ({ id, title, method, path, description, children }) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <MethodBadge method={method} />
          <code className="text-sm font-mono text-gray-800">{path}</code>
          <span className="text-sm text-gray-500 hidden md:inline">- {title}</span>
        </div>
        {expandedSections[id] ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {expandedSections[id] && (
        <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4">
          <p className="text-sm text-gray-700">{description}</p>
          {children}
        </div>
      )}
    </div>
  )

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <Code className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Developer API</h1>
        </div>
        <p className="text-gray-600 mt-2">
          Programmatically create and manage design templates. All endpoints require authentication via Bearer token.
        </p>
      </div>

      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Server className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Base URL</span>
          </div>
          <code className="text-sm text-blue-700 bg-blue-50 px-2 py-1 rounded block break-all">{baseUrl}</code>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Lock className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Authentication</span>
          </div>
          <code className="text-sm text-green-700 bg-green-50 px-2 py-1 rounded block">Bearer Token (JWT)</code>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <FileJson className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Content Type</span>
          </div>
          <code className="text-sm text-purple-700 bg-purple-50 px-2 py-1 rounded block">application/json</code>
        </div>
      </div>

      {/* Authentication Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
          <Key className="w-5 h-5 text-gray-600" />
          <span>Authentication</span>
        </h2>
        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
          <p className="text-sm text-gray-700">
            All API requests require a Bearer token in the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">Authorization</code> header.
            You can obtain a token by logging in via the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">POST /api/v1/auth/login</code> endpoint.
          </p>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Login to get token:</h4>
            <CodeBlock
              id="auth-login"
              code={`curl -X POST ${baseUrl}/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "your-email@example.com",
    "password": "your-password"
  }'`}
            />
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Using your token in requests:</h4>
            <CodeBlock
              id="auth-usage"
              code={`curl -X GET ${baseUrl}/templates/ \\
  -H "Authorization: Bearer YOUR_TOKEN_HERE" \\
  -H "Content-Type: application/json"`}
            />
          </div>

          {token && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-yellow-800">Your Current Token</h4>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowToken(!showToken)}
                    className="text-xs px-2 py-1 text-yellow-700 hover:bg-yellow-100 rounded border border-yellow-300 flex items-center space-x-1"
                  >
                    {showToken ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    <span>{showToken ? 'Hide' : 'Show'}</span>
                  </button>
                  <button
                    onClick={() => copyToClipboard(token, 'user-token')}
                    className="text-xs px-2 py-1 text-yellow-700 hover:bg-yellow-100 rounded border border-yellow-300 flex items-center space-x-1"
                  >
                    {copiedId === 'user-token' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    <span>Copy</span>
                  </button>
                </div>
              </div>
              <code className="text-xs text-yellow-700 break-all block">
                {showToken ? token : token.substring(0, 20) + '...' + token.substring(token.length - 10)}
              </code>
              <p className="text-xs text-yellow-600 mt-2">Logged in as: {user?.email || user?.mobile} ({user?.role})</p>
            </div>
          )}
        </div>
      </div>

      {/* Endpoints */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
          <Globe className="w-5 h-5 text-gray-600" />
          <span>API Endpoints</span>
        </h2>

        <div className="space-y-4">
          {/* Create Design */}
          <EndpointSection
            id="createDesign"
            title="Create Design"
            method="POST"
            path="/templates/developer/create-design"
            description="Create a new design template with full template_content. The design will be owned by the authenticated user."
          >
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Request Body Parameters</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-left border border-gray-200 rounded">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Parameter</th>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Type</th>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Required</th>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <ParamRow name="name" type="string" required description="Name of the design template" />
                    <ParamRow name="template_content" type="object" required description="Full template content JSON (pages, items, styles, dimensions, etc.)" />
                    <ParamRow name="description" type="string" required={false} description="Description of the design" />
                    <ParamRow name="category_id" type="string" required={false} description="UUID of the category to assign" />
                    <ParamRow name="sub_category_id" type="string" required={false} description="UUID of the sub-category" />
                    <ParamRow name="type" type="string" required={false} description="'image' or 'video'. Defaults to 'image'" />
                    <ParamRow name="tags" type="string[]" required={false} description="Array of tag strings" />
                    <ParamRow name="is_public" type="boolean" required={false} description="Whether the design is publicly visible. Defaults to false" />
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Example Request (cURL)</h4>
              <CodeBlock
                id="create-curl"
                code={`curl -X POST ${baseUrl}/templates/developer/create-design \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "My Wedding Invite",
    "description": "Beautiful wedding invitation template",
    "type": "image",
    "tags": ["wedding", "invitation"],
    "is_public": false,
    "template_content": {
      "width": 1080,
      "height": 1080,
      "type": "image",
      "pages": [
        {
          "id": "page-1",
          "background": "#ffffff",
          "items": [
            {
              "id": "text-1",
              "type": "text",
              "x": 100,
              "y": 200,
              "width": 880,
              "height": 100,
              "text": "You are Invited!",
              "fontSize": 48,
              "fontFamily": "Playfair Display",
              "fill": "#2c3e50",
              "textAlign": "center"
            },
            {
              "id": "image-1",
              "type": "image",
              "x": 340,
              "y": 400,
              "width": 400,
              "height": 400,
              "src": "https://example.com/photo.jpg",
              "opacity": 1,
              "borderRadius": 200
            }
          ]
        }
      ]
    }
  }'`}
              />
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Example Request (JavaScript)</h4>
              <CodeBlock
                id="create-js"
                code={`const response = await fetch('${baseUrl}/templates/developer/create-design', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_TOKEN',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'My Wedding Invite',
    type: 'image',
    is_public: false,
    template_content: {
      width: 1080,
      height: 1080,
      type: 'image',
      pages: [{
        id: 'page-1',
        background: '#ffffff',
        items: [
          {
            id: 'text-1',
            type: 'text',
            x: 100, y: 200,
            width: 880, height: 100,
            text: 'You are Invited!',
            fontSize: 48,
            fontFamily: 'Playfair Display',
            fill: '#2c3e50',
            textAlign: 'center'
          }
        ]
      }]
    }
  })
});

const data = await response.json();
console.log('Created design ID:', data.data.id);`}
              />
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Success Response (200)</h4>
              <CodeBlock
                id="create-response"
                code={`{
  "status": "success",
  "data": {
    "id": "abc123-uuid-here",
    "name": "My Wedding Invite",
    "description": "Beautiful wedding invitation template",
    "type": "image",
    "is_public": false,
    "stage": "pending",
    "template_content": { ... },
    "createdAt": "2026-08-21T10:30:00",
    "updatedAt": "2026-08-21T10:30:00"
  },
  "message": "Design created successfully via Developer API"
}`}
              />
            </div>
          </EndpointSection>

          {/* Toggle Visibility */}
          <EndpointSection
            id="visibility"
            title="Toggle Design Visibility"
            method="PUT"
            path="/templates/developer/{template_id}/visibility"
            description="Make a design public or private. Only the design owner or an admin/manager can change visibility."
          >
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">URL Parameters</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-left border border-gray-200 rounded">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Parameter</th>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Type</th>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Required</th>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <ParamRow name="template_id" type="string" required description="UUID of the design to update" />
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Request Body Parameters</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-left border border-gray-200 rounded">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Parameter</th>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Type</th>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Required</th>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <ParamRow name="is_public" type="boolean" required description="true = public, false = private" />
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Example Request (cURL)</h4>
              <CodeBlock
                id="visibility-curl"
                code={`# Make a design public
curl -X PUT ${baseUrl}/templates/developer/TEMPLATE_ID/visibility \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"is_public": true}'

# Make a design private
curl -X PUT ${baseUrl}/templates/developer/TEMPLATE_ID/visibility \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"is_public": false}'`}
              />
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Example Request (JavaScript)</h4>
              <CodeBlock
                id="visibility-js"
                code={`const templateId = 'abc123-uuid-here';

const response = await fetch(\`${baseUrl}/templates/developer/\${templateId}/visibility\`, {
  method: 'PUT',
  headers: {
    'Authorization': 'Bearer YOUR_TOKEN',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ is_public: true })
});

const data = await response.json();
console.log(data.message); // "Design is now public"`}
              />
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Success Response (200)</h4>
              <CodeBlock
                id="visibility-response"
                code={`{
  "status": "success",
  "data": {
    "id": "abc123-uuid-here",
    "name": "My Wedding Invite",
    "is_public": true
  },
  "message": "Design is now public"
}`}
              />
            </div>
          </EndpointSection>

          {/* List Templates */}
          <EndpointSection
            id="listTemplates"
            title="List Templates"
            method="GET"
            path="/templates/"
            description="Retrieve a paginated list of templates with various filtering options."
          >
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Query Parameters</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-left border border-gray-200 rounded">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Parameter</th>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Type</th>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Required</th>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-600">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <ParamRow name="search" type="string" required={false} description="Search by template name" />
                    <ParamRow name="category_id" type="string" required={false} description="Filter by category UUID" />
                    <ParamRow name="is_public" type="boolean" required={false} description="Filter by visibility" />
                    <ParamRow name="type" type="string" required={false} description="'image' or 'video'" />
                    <ParamRow name="stage" type="string" required={false} description="'pending', 'submitted', 'rejected', 'approved'" />
                    <ParamRow name="page" type="integer" required={false} description="Page number (default: 1)" />
                    <ParamRow name="limit" type="integer" required={false} description="Items per page (default: 50, max: 500)" />
                    <ParamRow name="order_by" type="string" required={false} description="Sort field (default: 'created_at')" />
                    <ParamRow name="order_desc" type="boolean" required={false} description="Sort descending (default: true)" />
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Example Request</h4>
              <CodeBlock
                id="list-curl"
                code={`curl -X GET "${baseUrl}/templates/?page=1&limit=10&is_public=true&type=image" \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
              />
            </div>
          </EndpointSection>

          {/* Get Template */}
          <EndpointSection
            id="getTemplate"
            title="Get Template by ID"
            method="GET"
            path="/templates/{template_id}"
            description="Retrieve full template data including template_content by its UUID."
          >
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Example Request</h4>
              <CodeBlock
                id="get-curl"
                code={`curl -X GET ${baseUrl}/templates/TEMPLATE_UUID \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
              />
            </div>
          </EndpointSection>

          {/* Update Template */}
          <EndpointSection
            id="updateTemplate"
            title="Update Template"
            method="PUT"
            path="/templates/{template_id}"
            description="Update template fields (name, description, tags, category, template_content, etc.). Only the owner or admin/manager can update."
          >
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Example Request</h4>
              <CodeBlock
                id="update-curl"
                code={`curl -X PUT ${baseUrl}/templates/TEMPLATE_UUID \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Updated Template Name",
    "description": "Updated description",
    "tags": ["wedding", "premium"],
    "is_public": true
  }'`}
              />
            </div>
          </EndpointSection>

          {/* Delete Template */}
          <EndpointSection
            id="deleteTemplate"
            title="Delete Template"
            method="DELETE"
            path="/templates/{template_id}"
            description="Soft-delete a template. Only the owner or admin can delete. The template can be recovered from the Trash."
          >
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Example Request</h4>
              <CodeBlock
                id="delete-curl"
                code={`curl -X DELETE ${baseUrl}/templates/TEMPLATE_UUID \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
              />
            </div>
          </EndpointSection>

          {/* Clone Template */}
          <EndpointSection
            id="cloneTemplate"
            title="Clone Template"
            method="POST"
            path="/templates/{template_id}/clone"
            description="Duplicate an existing template. The clone will be private by default and assigned to the authenticated user."
          >
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Example Request</h4>
              <CodeBlock
                id="clone-curl"
                code={`curl -X POST ${baseUrl}/templates/TEMPLATE_UUID/clone \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "My Copy of the Template"}'`}
              />
            </div>
          </EndpointSection>
        </div>
      </div>

      {/* Error Codes */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Error Codes</h2>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3 px-4 text-xs font-semibold text-gray-600">Status Code</th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-600">Meaning</th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-600">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr>
                <td className="py-3 px-4"><code className="text-sm bg-green-50 text-green-700 px-2 py-0.5 rounded">200</code></td>
                <td className="py-3 px-4 text-sm font-medium text-gray-700">OK</td>
                <td className="py-3 px-4 text-sm text-gray-600">Request succeeded</td>
              </tr>
              <tr>
                <td className="py-3 px-4"><code className="text-sm bg-red-50 text-red-700 px-2 py-0.5 rounded">400</code></td>
                <td className="py-3 px-4 text-sm font-medium text-gray-700">Bad Request</td>
                <td className="py-3 px-4 text-sm text-gray-600">Invalid request body or missing required fields</td>
              </tr>
              <tr>
                <td className="py-3 px-4"><code className="text-sm bg-red-50 text-red-700 px-2 py-0.5 rounded">401</code></td>
                <td className="py-3 px-4 text-sm font-medium text-gray-700">Unauthorized</td>
                <td className="py-3 px-4 text-sm text-gray-600">Missing or invalid Bearer token</td>
              </tr>
              <tr>
                <td className="py-3 px-4"><code className="text-sm bg-red-50 text-red-700 px-2 py-0.5 rounded">403</code></td>
                <td className="py-3 px-4 text-sm font-medium text-gray-700">Forbidden</td>
                <td className="py-3 px-4 text-sm text-gray-600">Insufficient permissions (e.g., trying to modify another user's template)</td>
              </tr>
              <tr>
                <td className="py-3 px-4"><code className="text-sm bg-red-50 text-red-700 px-2 py-0.5 rounded">404</code></td>
                <td className="py-3 px-4 text-sm font-medium text-gray-700">Not Found</td>
                <td className="py-3 px-4 text-sm text-gray-600">Template or resource not found</td>
              </tr>
              <tr>
                <td className="py-3 px-4"><code className="text-sm bg-red-50 text-red-700 px-2 py-0.5 rounded">500</code></td>
                <td className="py-3 px-4 text-sm font-medium text-gray-700">Server Error</td>
                <td className="py-3 px-4 text-sm text-gray-600">Internal server error</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Template Content Schema */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Template Content Schema</h2>
        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
          <p className="text-sm text-gray-700">
            The <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">template_content</code> object defines the full structure of a design.
            Below is the schema overview:
          </p>
          <CodeBlock
            id="schema"
            code={`{
  "width": 1080,            // Canvas width in pixels
  "height": 1080,           // Canvas height in pixels
  "type": "image",          // "image" or "video"
  "pages": [
    {
      "id": "page-1",       // Unique page identifier
      "background": "#ffffff", // Background color or image URL
      "items": [
        {
          "id": "item-1",         // Unique item identifier
          "type": "text",         // "text", "image", "shape", "video", "audio"
          "x": 0,                 // X position
          "y": 0,                 // Y position
          "width": 500,           // Width
          "height": 100,          // Height
          "rotation": 0,          // Rotation in degrees
          "opacity": 1,           // 0 to 1
          "locked": false,        // Lock element from editing
          "layer": 1,             // Z-index layer order

          // Text-specific:
          "text": "Hello World",
          "fontSize": 32,
          "fontFamily": "Arial",
          "fill": "#000000",
          "textAlign": "center",  // "left", "center", "right"
          "fontWeight": "bold",
          "fontStyle": "normal",  // "normal", "italic"

          // Image-specific:
          "src": "https://...",   // Image URL
          "borderRadius": 0,

          // Shape-specific:
          "shapeType": "rectangle", // "rectangle", "circle", "triangle", etc.
          "fill": "#3498db",
          "stroke": "#2c3e50",
          "strokeWidth": 2
        }
      ]
    }
  ]
}`}
          />
        </div>
      </div>

      {/* Rate Limits */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Rate Limits & Notes</h2>
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start space-x-2">
              <span className="text-blue-500 mt-0.5">&#8226;</span>
              <span>Token expires after <strong>7 days</strong>. Re-authenticate to get a new token.</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-blue-500 mt-0.5">&#8226;</span>
              <span>Maximum request body size: <strong>10 MB</strong>.</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-blue-500 mt-0.5">&#8226;</span>
              <span>The <code className="bg-gray-100 px-1.5 py-0.5 rounded">template_content</code> JSON field has no hard schema validation - ensure you structure it correctly for the editor to render properly.</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-blue-500 mt-0.5">&#8226;</span>
              <span>Newly created designs default to <strong>stage: "pending"</strong> and <strong>is_public: false</strong>.</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-blue-500 mt-0.5">&#8226;</span>
              <span>Only design owners, managers, and admins can toggle visibility of a design.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default DeveloperApiPage
